import { useEffect, useMemo, useState } from 'react';
import { assetsApi, type Asset, type AssetCategory, type CreateAssetInput, type DepreciationMethod } from '../lib/api/assets';
import { employeesApi, type EmployeePickerItem } from '../lib/api/employees';
import { departmentsApi, type Department } from '../lib/api/departments';
import { SearchableDropdown } from './SearchableDropdown';
import { attachmentsApi, type UploadedAttachment } from '../lib/api/attachments';
import { AttachmentImagePreview } from './AttachmentImagePreview';

interface Props {
  open: boolean;
  editingId?: string | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}

// TK nguyên giá + TK khấu hao — chuẩn TT200.
const COST_ACCOUNTS: Array<{ code: string; label: string }> = [
  { code: '2111', label: '2111 · TSCĐ hữu hình' },
  { code: '2112', label: '2112 · Nhà cửa vật kiến trúc' },
  { code: '2113', label: '2113 · Máy móc thiết bị' },
  { code: '2114', label: '2114 · Thiết bị/Nội thất VP' },
  { code: '2135', label: '2135 · TSCĐ vô hình (BQ PM)' },
];

const DEPR_ACCOUNTS: Array<{ code: string; label: string }> = [
  { code: '2141', label: '2141 · Hao mòn TSCĐ hữu hình' },
  { code: '2145', label: '2145 · Hao mòn TSCĐ vô hình' },
];

const DEPR_METHODS: Array<{ code: DepreciationMethod; label: string }> = [
  { code: 'straight', label: 'Đường thẳng (SLN)' },
  { code: 'declining', label: 'Số dư giảm dần' },
  { code: 'units', label: 'Theo sản lượng' },
];
const MAX_ASSET_IMAGE_BYTES = 5 * 1024 * 1024;

function fmtVND(n: number): string {
  return n.toLocaleString('vi-VN');
}

function toIsoDate(v: string | null | undefined): string {
  if (!v) return '';
  return v.slice(0, 10);
}

export function AssetFormModal({ open, editingId, onClose, onSaved, onDeleted }: Props) {
  const isEdit = Boolean(editingId);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<EmployeePickerItem[]>([]);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Section 1: Thông tin cơ bản
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [categoryCode, setCategoryCode] = useState('MT');
  const [serialNumber, setSerialNumber] = useState('');

  // Section 2: Mua sắm
  const [supplier, setSupplier] = useState('');
  const [purchasedAt, setPurchasedAt] = useState(new Date().toISOString().slice(0, 10));
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [costInput, setCostInput] = useState('');
  const [warrantyUntil, setWarrantyUntil] = useState('');

  // Section 3: Cấu hình khấu hao
  const [depreciationMethod, setDepreciationMethod] = useState<DepreciationMethod>('straight');
  const [depreciationYears, setDepreciationYears] = useState<number | ''>('');
  const [depreciationStartDate, setDepreciationStartDate] = useState('');
  const [costAccount, setCostAccount] = useState('2111');
  const [depreciationAccount, setDepreciationAccount] = useState('2141');

  // Section 4: Gán cho
  const [department, setDepartment] = useState('Software');
  const [assignedEmployeeId, setAssignedEmployeeId] = useState('');
  const [initialAssignedEmployeeId, setInitialAssignedEmployeeId] = useState('');
  const [legacyUserName, setLegacyUserName] = useState('');

  // Section 5: Ghi chú
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState<UploadedAttachment[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Init on open
  useEffect(() => {
    if (!open) return;
    setErr(null); setSaving(false); setDeleting(false);
    // Reset form
    setCode(''); setName(''); setCategoryCode('MT'); setSerialNumber('');
    setSupplier(''); setPurchasedAt(new Date().toISOString().slice(0, 10));
    setInvoiceNumber(''); setCostInput(''); setWarrantyUntil('');
    setDepreciationMethod('straight'); setDepreciationYears('');
    setDepreciationStartDate(''); setCostAccount('2111'); setDepreciationAccount('2141');
    setDepartment('Software'); setAssignedEmployeeId(''); setInitialAssignedEmployeeId(''); setLegacyUserName('');
    setNotes('');
    setImages([]); setUploadingImages(false);

    assetsApi.listCategories().then((r) => setCategories(r.items)).catch(() => {});
    departmentsApi.list().then((r) => setDepartments(r.items)).catch(() => setDepartments([]));
    employeesApi.picker().then(setEmployees).catch(() => setEmployees([]));

    if (editingId) {
      setLoadingEdit(true);
      assetsApi.findOne(editingId)
        .then((a) => fillFromAsset(a))
        .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoadingEdit(false));
    }
  }, [open, editingId]);

  const fillFromAsset = (a: Asset) => {
    setCode(a.code);
    setName(a.name);
    setCategoryCode(a.categoryCode);
    setSerialNumber(a.serialNumber ?? '');
    setSupplier(a.supplier ?? '');
    setPurchasedAt(toIsoDate(a.purchasedAt));
    setInvoiceNumber(a.invoiceNumber ?? '');
    setCostInput(String(a.cost));
    setWarrantyUntil(toIsoDate(a.warrantyUntil));
    setDepreciationMethod(a.depreciationMethod ?? 'straight');
    setDepreciationYears(a.depreciationYears ?? '');
    setDepreciationStartDate(toIsoDate(a.depreciationStartDate));
    setCostAccount(a.costAccount ?? '2111');
    setDepreciationAccount(a.depreciationAccount ?? '2141');
    setDepartment(a.department ?? 'Software');
    const existingEmployeeId = a.assignedEmployeeId ?? '';
    setAssignedEmployeeId(existingEmployeeId);
    setInitialAssignedEmployeeId(existingEmployeeId);
    setLegacyUserName(a.userName ?? '');
    setNotes(a.notes ?? '');
    setImages(a.images ?? []);
  };

  // Category chọn hiện tại → dùng cho placeholder KH years
  const selectedCat = useMemo(
    () => categories.find((c) => c.code === categoryCode),
    [categories, categoryCode],
  );
  const departmentEmployees = useMemo(
    () => employees.filter((employee) => !department || employee.departmentName === department),
    [department, employees],
  );

  const cost = Number(costInput.replace(/\D/g, '')) || 0;
  const effectiveYears = depreciationYears === '' ? selectedCat?.years ?? 0 : depreciationYears;

  // Auto-calc: chỉ hiển thị cho phương pháp đường thẳng.
  const monthlyDepr = useMemo(() => {
    if (depreciationMethod !== 'straight') return null;
    if (!cost || !effectiveYears) return null;
    return Math.round(cost / (effectiveYears * 12));
  }, [cost, effectiveYears, depreciationMethod]);

  const yearlyDepr = useMemo(() => {
    if (depreciationMethod !== 'straight') return null;
    if (!cost || !effectiveYears) return null;
    return Math.round(cost / effectiveYears);
  }, [cost, effectiveYears, depreciationMethod]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!isEdit && !/^TS\d{4,}$/i.test(code.trim())) return setErr('Mã TS phải theo pattern TS + 4 số (VD TS0008).');
    if (name.trim().length < 2) return setErr('Tên tài sản tối thiểu 2 ký tự.');
    if (cost < 1_000_000) return setErr('Nguyên giá tối thiểu 1.000.000đ.');

    const assignmentPayload = !isEdit || assignedEmployeeId !== initialAssignedEmployeeId
      ? { assignedEmployeeId: assignedEmployeeId || '' }
      : {};
    const payload: CreateAssetInput = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      categoryCode,
      department: department.trim() || undefined,
      ...assignmentPayload,
      purchasedAt,
      cost,
      notes: notes.trim() || undefined,
      serialNumber: serialNumber.trim() || undefined,
      supplier: supplier.trim() || undefined,
      invoiceNumber: invoiceNumber.trim() || undefined,
      warrantyUntil: warrantyUntil || undefined,
      depreciationMethod,
      depreciationYears: depreciationYears === '' ? undefined : Number(depreciationYears),
      depreciationStartDate: depreciationStartDate || undefined,
      costAccount: costAccount || undefined,
      depreciationAccount: depreciationAccount || undefined,
      imageAttachmentIds: images.map((image) => image.id),
    };

    setSaving(true);
    try {
      if (isEdit && editingId) {
        // Edit: không đổi code, gửi Partial để service dùng logic $undefined skip.
        const { code: _c, ...rest } = payload;
        await assetsApi.update(editingId, rest);
      } else {
        await assetsApi.create(payload);
      }
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally { setSaving(false); }
  };

  const uploadImages = async (files: FileList | null) => {
    const picked = Array.from(files ?? []);
    if (!picked.length) return;
    if (images.length + picked.length > 5) return setErr('Mỗi tài sản tối đa 5 hình ảnh.');
    if (picked.some((file) => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type))) return setErr('Chỉ hỗ trợ ảnh JPG, PNG hoặc WEBP.');
    if (picked.some((file) => file.size > MAX_ASSET_IMAGE_BYTES)) return setErr('Mỗi hình ảnh tài sản tối đa 5MB.');
    setErr(null); setUploadingImages(true);
    try {
      const uploaded = await Promise.all(picked.map((file) => attachmentsApi.upload(file)));
      setImages((current) => [...current, ...uploaded]);
    }
    catch (error) { setErr(error instanceof Error ? error.message : 'Không thể upload hình ảnh.'); }
    finally { setUploadingImages(false); }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm(`Xoá TS ${code} - ${name}?`)) return;
    setDeleting(true);
    setErr(null);
    try {
      await assetsApi.remove(editingId);
      onDeleted?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setDeleting(false); }
  };

  return (
    <div onClick={onClose} style={backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalBox}>
        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
          {/* Header */}
          <div style={header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={headerIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M8 12h8M12 8v8" /></svg>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#172033' }}>
                  {isEdit ? 'Sửa tài sản' : 'Thêm tài sản mới'}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#667085' }}>Điền thông tin tài sản + cấu hình khấu hao</p>
              </div>
            </div>
            <button type="button" onClick={onClose} style={closeBtn} aria-label="Đóng">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Body */}
          <div style={body}>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

              {loadingEdit && <div style={{ padding: 12, color: '#64748b', fontSize: 13 }}>Đang tải dữ liệu…</div>}

              {/* Section 1: Thông tin cơ bản */}
              <Section title="Thông tin cơ bản">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                  <Field label="Mã TS" required>
                    <input
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder="TS0011"
                      disabled={isEdit}
                      style={{ ...input, fontWeight: 700 }}
                    />
                  </Field>
                  <Field label="Tên tài sản" required>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="VD MacBook Pro 14 M4 Max" style={{ ...input, fontSize: 14 }} required />
                  </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                  <Field label="Loại tài sản">
                    <SearchableDropdown value={categoryCode} onChange={setCategoryCode} ariaLabel="Loại tài sản" options={categories.map((c) => ({ value: c.code, label: `${c.code} · ${c.name} (KH ${c.years} năm)` }))} />
                  </Field>
                  <Field label="Serial number">
                    <input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="VD: C02XG2JZLY8H" style={input} />
                  </Field>
                </div>
              </Section>

              <Section title={`Hình ảnh tài sản (${images.length}/5)`}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {images.map((image) => (
                    <div key={image.id} style={{ width: 150, border: '1px solid #dbe3ee', borderRadius: 8, padding: 8, background: '#f8fafc' }}>
                      <button type="button" onClick={() => attachmentsApi.openInNewTab(image.id, image.fileName).catch((error) => setErr(error instanceof Error ? error.message : 'Không thể mở ảnh.'))} style={{ width: '100%', height: 56, padding: 0, border: 0, borderRadius: 6, overflow: 'hidden', background: '#dbeafe', color: '#2563eb', cursor: 'pointer' }}><AttachmentImagePreview attachment={image} alt={image.fileName} style={{ height: 56 }} /></button>
                      <div title={image.fileName} style={{ marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: '#334155' }}>{image.fileName}</div>
                      <button type="button" onClick={() => setImages((current) => current.filter((item) => item.id !== image.id))} style={{ marginTop: 5, padding: 0, border: 0, background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>Bỏ ảnh</button>
                    </div>
                  ))}
                  {images.length < 5 && <label style={{ width: 150, minHeight: 96, border: '1px dashed #93c5fd', borderRadius: 8, background: '#eff6ff', color: '#1d4ed8', display: 'grid', placeItems: 'center', textAlign: 'center', padding: 10, cursor: uploadingImages ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600 }}><input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={uploadingImages} onChange={(event) => { void uploadImages(event.target.files); event.currentTarget.value = ''; }} />{uploadingImages ? 'Đang upload…' : '+ Thêm hình ảnh'}</label>}
                </div>
                <div style={{ marginTop: 8, color: '#64748b', fontSize: 11 }}>JPG, PNG hoặc WEBP · tối đa 5MB/ảnh · tối đa 5 ảnh.</div>
              </Section>

              {/* Section 2: Mua sắm */}
              <Section title="Mua sắm">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <Field label="Nhà cung cấp">
                    <input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="VD: FPT Shop" style={input} />
                  </Field>
                  <Field label="Ngày mua" required>
                    <input type="date" value={purchasedAt} onChange={(e) => setPurchasedAt(e.target.value)} style={input} required />
                  </Field>
                  <Field label="Số hoá đơn">
                    <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="HD-XXX-YYYY-####" style={input} />
                  </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12, marginTop: 12 }}>
                  <Field label="Nguyên giá" required>
                    <div style={{ position: 'relative' }}>
                      <input
                        inputMode="numeric"
                        placeholder="VD 55.000.000"
                        value={cost ? fmtVND(cost) : ''}
                        onChange={(e) => setCostInput(e.target.value)}
                        style={{ ...input, height: 42, fontSize: 18, fontWeight: 700, textAlign: 'right', paddingRight: 32 }}
                        required
                      />
                      <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 14 }}>đ</span>
                    </div>
                  </Field>
                  <Field label="Bảo hành đến">
                    <input type="date" value={warrantyUntil} onChange={(e) => setWarrantyUntil(e.target.value)} style={{ ...input, height: 42 }} />
                  </Field>
                </div>
              </Section>

              {/* Section 3: Cấu hình khấu hao */}
              <Section title="Cấu hình khấu hao">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <Field label="Phương pháp KH">
                    <SearchableDropdown value={depreciationMethod} onChange={(value) => setDepreciationMethod(value as DepreciationMethod)} ariaLabel="Phương pháp khấu hao" options={DEPR_METHODS.map((m) => ({ value: m.code, label: m.label }))} />
                  </Field>
                  <Field label="Thời gian KH (năm)">
                    <input
                      type="number" min={1} max={50}
                      value={depreciationYears}
                      onChange={(e) => setDepreciationYears(e.target.value === '' ? '' : Math.max(1, Math.min(50, Number(e.target.value))))}
                      placeholder={selectedCat ? `Mặc định ${selectedCat.years}` : ''}
                      style={{ ...input, textAlign: 'right', fontWeight: 600 }}
                    />
                  </Field>
                  <Field label="Ngày bắt đầu KH">
                    <input type="date" value={depreciationStartDate} onChange={(e) => setDepreciationStartDate(e.target.value)} style={input} />
                  </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                  <Field label="TK nguyên giá">
                    <SearchableDropdown value={costAccount} onChange={setCostAccount} ariaLabel="Tài khoản nguyên giá" options={COST_ACCOUNTS.map((a) => ({ value: a.code, label: a.label }))} />
                  </Field>
                  <Field label="TK khấu hao">
                    <SearchableDropdown value={depreciationAccount} onChange={setDepreciationAccount} ariaLabel="Tài khoản khấu hao" options={DEPR_ACCOUNTS.map((a) => ({ value: a.code, label: a.label }))} />
                  </Field>
                </div>
                {monthlyDepr != null && yearlyDepr != null && (
                  <div style={hintBox}>
                    <b>Tự tính:</b> KH mỗi tháng = {fmtVND(cost)} / ({effectiveYears}×12) = <b>{fmtVND(monthlyDepr)}đ/tháng</b> · Hoặc <b>{fmtVND(yearlyDepr)}đ/năm</b>
                  </div>
                )}
                {depreciationMethod !== 'straight' && (
                  <div style={{ ...hintBox, background: '#fefce8', borderLeftColor: '#ca8a04', color: '#854d0e' }}>
                    <b>Lưu ý:</b> Auto-tính chỉ hỗ trợ phương pháp <b>Đường thẳng</b>. Các phương pháp khác cần tính tay theo TT45/2013.
                  </div>
                )}
              </Section>

              {/* Section 4: Gán cho */}
              <Section title="Gán cho">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Bộ phận">
                    <SearchableDropdown value={department} onChange={(value) => { setDepartment(value); setAssignedEmployeeId(''); }} ariaLabel="Bộ phận" options={Array.from(new Set([...departments.map((item) => item.name), department].filter(Boolean))).map((item) => ({ value: item, label: item }))} />
                  </Field>
                  <Field label="Nhân viên sử dụng">
                    <SearchableDropdown
                      value={assignedEmployeeId}
                      onChange={(id) => {
                        setAssignedEmployeeId(id);
                        const employee = employees.find((item) => item.id === id);
                        if (employee?.departmentName) setDepartment(employee.departmentName);
                      }}
                      placeholder="Chưa gán / Dùng chung"
                      ariaLabel="Nhân viên sử dụng"
                      options={[{ value: '', label: 'Chưa gán / Dùng chung' }, ...departmentEmployees.map((employee) => ({ value: employee.id, label: `${employee.code ? `${employee.code} · ` : ''}${employee.name}${employee.departmentName ? ` · ${employee.departmentName}` : ''}` }))]}
                    />
                    <div style={{ marginTop: 5, fontSize: 11, color: '#64748b' }}>Hiển thị {departmentEmployees.length} nhân viên đang hoạt động thuộc bộ phận {department}.</div>
                    {!assignedEmployeeId && isEdit && Boolean(legacyUserName) && <div style={{ marginTop: 5, fontSize: 11, color: '#a16207' }}>Dữ liệu cũ: {legacyUserName}. Hãy chọn nhân viên HR để chuẩn hoá bàn giao.</div>}
                  </Field>
                </div>
              </Section>

              {/* Section 5: Ghi chú */}
              <Section title="Ghi chú">
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ghi chú về tài sản, tình trạng, đặc điểm..."
                  style={{ ...input, height: 'auto', padding: '10px 12px', resize: 'vertical', lineHeight: 1.5 }}
                />
              </Section>

              {err && <div style={errBox}>{err}</div>}
            </div>
          </div>

          {/* Footer */}
          <div style={footer}>
            {isEdit ? (
              <button type="button" onClick={handleDelete} disabled={deleting || saving} style={deleteBtn}>
                {deleting ? 'Đang xoá…' : 'Xoá tài sản'}
              </button>
            ) : <div />}
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button type="button" onClick={onClose} disabled={saving || deleting} style={cancelBtn}>Huỷ</button>
              <button type="submit" disabled={saving || deleting || loadingEdit || uploadingImages} style={saveBtn}>
                {saving ? 'Đang lưu…' : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                    {isEdit ? 'Lưu thay đổi' : 'Lưu tài sản'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==== Sub components ====

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#667085', fontWeight: 700, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: '#334155', fontWeight: 600, marginBottom: 6 }}>
        {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ==== Styles ====

const backdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,.55)',
  backdropFilter: 'blur(4px)',
  zIndex: 60,
  padding: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const modalBox: React.CSSProperties = {
  width: 820,
  maxWidth: '100%',
  height: 'calc(100vh - 48px)',
  maxHeight: 860,
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 25px 60px -12px rgba(15,23,42,.5)',
  overflow: 'hidden',
};

const header: React.CSSProperties = {
  flex: '0 0 auto',
  padding: '18px 24px',
  borderBottom: '1px solid #e4e7ec',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const headerIcon: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 9,
  background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const closeBtn: React.CSSProperties = {
  width: 32,
  height: 32,
  background: '#fff',
  border: '1px solid #e4e7ec',
  borderRadius: 8,
  cursor: 'pointer',
  color: '#64748b',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const body: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  background: '#f8fafc',
  minHeight: 0,
};

const footer: React.CSSProperties = {
  flex: '0 0 auto',
  padding: '14px 24px',
  borderTop: '1px solid #e4e7ec',
  background: '#fff',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
};

const input: React.CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  border: '1px solid #e4e7ec',
  borderRadius: 8,
  fontSize: 13,
  color: '#172033',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
};


const hintBox: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 14px',
  background: '#eff6ff',
  borderLeft: '3px solid #2563EB',
  borderRadius: '0 6px 6px 0',
  fontSize: 12,
  color: '#1e3a8a',
  lineHeight: 1.5,
};

const errBox: React.CSSProperties = {
  padding: '10px 14px',
  background: '#fef2f2',
  border: '1px solid #fca5a5',
  borderRadius: 8,
  color: '#b91c1c',
  fontSize: 13,
};

const deleteBtn: React.CSSProperties = {
  padding: '9px 14px',
  background: '#fff',
  border: '1px solid #fca5a5',
  color: '#b91c1c',
  fontSize: 13,
  fontWeight: 500,
  borderRadius: 8,
  cursor: 'pointer',
};

const cancelBtn: React.CSSProperties = {
  padding: '9px 16px',
  background: '#fff',
  border: '1px solid #e4e7ec',
  color: '#334155',
  fontSize: 13,
  fontWeight: 500,
  borderRadius: 8,
  cursor: 'pointer',
};

const saveBtn: React.CSSProperties = {
  padding: '9px 20px',
  background: '#1d2939',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
